/**
 * Team Members Modal Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Modal dialog for managing team members.
 */

import React, { useState, useEffect } from 'react';
import { X, UserPlus, Trash2, Crown } from 'lucide-react';
import type { Team, TeamMemberSummary, UserListItem } from '../../../types/admin';
import { teamService, userManagementService } from '../../../services/admin';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../../utils/apiErrorHandler';

interface TeamMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
  onMembersChanged?: () => void;
}

const TeamMembersModal: React.FC<TeamMembersModalProps> = ({
  isOpen,
  onClose,
  team,
  onMembersChanged,
}) => {
  const [members, setMembers] = useState<TeamMemberSummary[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserListItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Load team members and available users
  useEffect(() => {
    if (isOpen && team) {
      setIsLoading(true);
      Promise.all([
        teamService.getTeamMembers(team.id),
        userManagementService.listUsers({ is_active: true, page_size: 100 }),
      ])
        .then(([membersData, usersResponse]) => {
          setMembers(membersData);
          // Filter out users already in the team
          const memberIds = new Set(membersData.map((m) => m.user_id));
          setAvailableUsers(usersResponse.users.filter((u) => !memberIds.has(u.id)));
        })
        .catch((err) => {
          console.error('Failed to load data:', err);
          toast.error('Failed to load team members');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, team]);

  const handleAddMember = async () => {
    if (!team || !selectedUserId) return;

    setIsAdding(true);
    try {
      const newMember = await teamService.addTeamMember(team.id, {
        user_id: selectedUserId,
        role: 'member',
      });
      setMembers([...members, newMember]);
      setAvailableUsers(availableUsers.filter((u) => u.id !== selectedUserId));
      setSelectedUserId('');
      toast.success('Member added successfully');
      onMembersChanged?.();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to add member'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!team) return;

    setRemovingId(userId);
    try {
      await teamService.removeTeamMember(team.id, userId);
      const removedMember = members.find((m) => m.user_id === userId);
      setMembers(members.filter((m) => m.user_id !== userId));
      if (removedMember) {
        setAvailableUsers([
          ...availableUsers,
          {
            id: removedMember.user_id,
            email: removedMember.email,
            display_name: removedMember.display_name,
            role: removedMember.user_role,
            is_active: true,
          } as UserListItem,
        ]);
      }
      toast.success('Member removed successfully');
      onMembersChanged?.();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to remove member'));
    } finally {
      setRemovingId(null);
    }
  };

  const handleChangeRole = async (userId: string, newRole: 'lead' | 'member') => {
    if (!team) return;

    try {
      await teamService.changeTeamMemberRole(team.id, userId, newRole);
      setMembers(
        members.map((m) =>
          m.user_id === userId ? { ...m, role: newRole } : m
        )
      );
      toast.success('Role updated successfully');
      onMembersChanged?.();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update role'));
    }
  };

  if (!isOpen || !team) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
              <p className="text-sm text-gray-500">{team.name}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {/* Add member */}
            <div className="flex gap-2 mb-4">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                disabled={isLoading || availableUsers.length === 0}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] disabled:bg-gray-50"
              >
                <option value="">
                  {availableUsers.length === 0
                    ? 'No users available'
                    : 'Select a user to add...'}
                </option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name || user.email}
                  </option>
                ))}
              </select>
              <Button
                onClick={handleAddMember}
                disabled={!selectedUserId || isAdding}
                size="sm"
              >
                <UserPlus size={16} className="mr-1" />
                Add
              </Button>
            </div>

            {/* Members list */}
            {isLoading ? (
              <div className="py-8 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-gray-300 border-t-[#002B45] rounded-full mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading members...</p>
              </div>
            ) : members.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No members in this team yet
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {members.map((member) => (
                  <div
                    key={member.user_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#002B45] text-white flex items-center justify-center text-sm font-medium">
                        {(member.display_name || member.email)[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {member.display_name || member.email}
                          </span>
                          {member.role === 'lead' && (
                            <Crown size={14} className="text-[#F7B500]" />
                          )}
                        </div>
                        <span className="text-xs text-gray-500 capitalize">
                          {member.user_role}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <select
                        value={member.role}
                        onChange={(e) =>
                          handleChangeRole(member.user_id, e.target.value as 'lead' | 'member')
                        }
                        className="text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[#F7B500]"
                      >
                        <option value="member">Member</option>
                        <option value="lead">Lead</option>
                      </select>
                      <button
                        onClick={() => handleRemoveMember(member.user_id)}
                        disabled={removingId === member.user_id}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                        title="Remove member"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMembersModal;
